import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Star, Search, Filter, SortAsc, SortDesc, Loader2 } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import Seo from "@/seo/seo"
import { routeSeoConfig } from "@/utils/seo-config"
import axios from "@/utils/axios"
import { toast } from "sonner"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationLink,
  PaginationNext,
} from "@/components/ui/pagination"

export default function AllReviews() {
  const [allReviews, setAllReviews] = useState([])
  const [stats, setStats] = useState({ averageRating: "0.0", totalReviews: 0, ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } })
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const defaultReviews = [
    {
      id: 1,
      name: "Sarah L.",
      avatar: "/placeholder.svg?height=50&width=50",
      rating: 5,
      title: "Absolutely Transformed My Skin!",
      review:
        "Fayoz products have transformed my skin! My complexion is clearer and more radiant than ever before. Highly recommend the Hydrating Face Cream! It absorbs beautifully and leaves no greasy residue. I've received so many compliments since I started using it.",
      date: "2024-07-10",
    },
    {
      id: 2,
      name: "David P.",
      avatar: "/placeholder.svg?height=50&width=50",
      rating: 4,
      title: "Gentle and Effective Cleanser",
      review:
        "I've struggled with sensitive skin for years, but Fayoz's Gentle Cleanser is a game-changer. No irritation, just clean, soft skin. Fantastic! It removes makeup effortlessly without stripping natural oils.",
      date: "2024-07-08",
    },
    {
      id: 3,
      name: "Emily R.",
      avatar: "/placeholder.svg?height=50&width=50",
      rating: 5,
      title: "Youth in a Bottle!",
      review:
        "The Anti-Aging Serum is incredible! I've noticed a significant reduction in fine lines around my eyes. It feels luxurious and works wonders. My skin feels firmer and looks more plump.",
      date: "2024-07-05",
    },
    {
      id: 4,
      name: "Michael B.",
      avatar: "/placeholder.svg?height=50&width=50",
      rating: 5,
      title: "Best Sunscreen Ever!",
      review:
        "Finally, a sunscreen that doesn't feel greasy! The SPF 30 Sunscreen is lightweight and provides excellent protection. A daily essential for me. It sits perfectly under makeup too.",
      date: "2024-07-03",
    },
    {
      id: 5,
      name: "Jessica T.",
      avatar: "/placeholder.svg?height=50&width=50",
      rating: 5,
      title: "Radiant Complexion!",
      review:
        "The Vitamin C Serum brightened my skin tone dramatically. It's now a staple in my morning routine. Love the glow! My dark spots have visibly faded.",
      date: "2024-07-01",
    },
    {
      id: 6,
      name: "Chris W.",
      avatar: "/placeholder.svg?height=50&width=50",
      rating: 4,
      title: "Nourishing Night Cream",
      review:
        "The Night Repair Cream is so nourishing. My skin feels incredibly soft and hydrated when I wake up. Great product! A little goes a long way.",
      date: "2024-06-28",
    },
    {
      id: 7,
      name: "Olivia M.",
      avatar: "/placeholder.svg?height=50&width=50",
      rating: 3,
      title: "Good, but not for me",
      review:
        "The product was okay, but I didn't see the dramatic results others mentioned. It might work better for different skin types. Still, it felt nice on the skin.",
      date: "2024-06-25",
    },
    {
      id: 8,
      name: "Daniel K.",
      avatar: "/placeholder.svg?height=50&width=50",
      rating: 5,
      title: "Amazing Hydration!",
      review:
        "This moisturizer is a lifesaver for my dry skin. It provides deep hydration without feeling heavy. My skin feels plump and healthy all day.",
      date: "2024-06-20",
    },
    {
      id: 9,
      name: "Sophia G.",
      avatar: "/placeholder.svg?height=50&width=50",
      rating: 4,
      title: "Effective Exfoliator",
      review:
        "The exfoliating scrub is gentle yet effective. My skin feels smooth and refreshed after each use. Just be careful not to over-exfoliate.",
      date: "2024-06-18",
    },
    {
      id: 10,
      name: "Liam F.",
      avatar: "/placeholder.svg?height=50&width=50",
      rating: 5,
      title: "Perfect for Oily Skin",
      review:
        "As someone with oily skin, finding the right products is hard. This toner controls shine and minimizes pores without drying out my skin. Love it!",
      date: "2024-06-15",
    },
  ]

  const [searchTerm, setSearchTerm] = useState("")
  const [filterRating, setFilterRating] = useState("all")
  const [sortBy, setSortBy] = useState("newest")
  const [currentPage, setCurrentPage] = useState(1)
  const [reviewForm, setReviewForm] = useState({ title: "", review: "", rating: "" })
  const reviewsPerPage = 6
  const [pagination, setPagination] = useState({ page: 1, limit: reviewsPerPage, total: 0, totalPages: 0 })

  // Fetch reviews from API
  useEffect(() => {
    const fetchReviews = async () => {
      setLoading(true)
      try {
        const params = {
          page: currentPage,
          limit: reviewsPerPage,
        }
        if (filterRating !== "all") {
          params.rating = filterRating
        }
        if (searchTerm) {
          params.search = searchTerm
        }
        const res = await axios.get("/reviews", { params })
        let reviews = res.data.reviews || []
        
        // Apply client-side sorting if needed (backend doesn't support sort parameter yet)
        if (sortBy === "oldest") {
          reviews = [...reviews].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        } else if (sortBy === "highest_rating") {
          reviews = [...reviews].sort((a, b) => b.rating - a.rating)
        } else if (sortBy === "lowest_rating") {
          reviews = [...reviews].sort((a, b) => a.rating - b.rating)
        }
        // newest is default from backend
        
        setAllReviews(reviews)
        setStats(res.data.stats || stats)
        setPagination(res.data.pagination || pagination)
      } catch (error) {
        console.error("Error fetching reviews:", error)
        // Use default reviews on error
        setAllReviews(defaultReviews)
      } finally {
        setLoading(false)
      }
    }
    fetchReviews()
  }, [currentPage, filterRating, searchTerm, sortBy])

  const handleSubmitReview = async (e) => {
    e.preventDefault()
    if (!reviewForm.title || !reviewForm.review || !reviewForm.rating) {
      toast.error("Please fill in all fields")
      return
    }
    setSubmitting(true)
    try {
      await axios.post("/reviews", {
        name: "Guest User", // You might want to get this from auth context
        rating: parseInt(reviewForm.rating),
        title: reviewForm.title,
        review: reviewForm.review,
        date: new Date().toISOString().split("T")[0],
      })
      toast.success("Review submitted successfully!")
      setReviewForm({ title: "", review: "", rating: "" })
      // Refresh reviews
      const res = await axios.get("/reviews", { params: { page: currentPage, limit: reviewsPerPage } })
      setAllReviews(res.data.reviews || [])
      setStats(res.data.stats || stats)
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to submit review")
    } finally {
      setSubmitting(false)
    }
  }

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const overallRating = stats.averageRating || "0.0"
  const ratingDistribution = stats.ratingDistribution || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  const currentReviews = allReviews
  const totalPages = pagination.totalPages || 1

  return (
    <>
      <Seo {...routeSeoConfig.reviews} type="website" />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold text-center mb-12 text-gray-900">Customer Reviews</h1>

      <div className="grid md:grid-cols-3 gap-8 mb-12">
        {/* Overall Rating Summary */}
        <Card className="col-span-full md:col-span-1 p-6 flex flex-col items-center justify-center bg-gradient-to-br from-yellow-50 to-yellow-100">
          <CardTitle className="text-2xl font-bold mb-2 text-gray-800">Overall Rating</CardTitle>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-5xl font-extrabold text-yellow-600">{overallRating}</span>
            <Star className="w-8 h-8 fill-yellow-600 text-yellow-600" />
          </div>
          <p className="text-gray-700 text-lg">{stats.totalReviews || allReviews.length} Reviews</p>
          <div className="w-full mt-4 space-y-1">
            {[5, 4, 3, 2, 1].map((star) => (
              <div key={star} className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">
                  {star} <Star className="w-3 h-3 fill-yellow-500 text-yellow-500 inline-block" />
                </span>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-yellow-400 h-2.5 rounded-full"
                    style={{ width: `${(ratingDistribution[star] / (stats.totalReviews || 1)) * 100 || 0}%` }}
                  ></div>
                </div>
                <span className="text-sm text-gray-600">({ratingDistribution[star] || 0})</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Review Submission Form */}
        <Card className="col-span-full md:col-span-2 p-6">
          <CardTitle className="text-2xl font-bold mb-6 text-gray-800">Write a Review</CardTitle>
          <form className="space-y-4" onSubmit={handleSubmitReview}>
            <div>
              <Label htmlFor="review-title">Review Title</Label>
              <Input 
                id="review-title" 
                placeholder="Summarize your experience" 
                value={reviewForm.title}
                onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="review-text">Your Review</Label>
              <Textarea 
                id="review-text" 
                placeholder="Share your thoughts on the product..." 
                rows={5}
                value={reviewForm.review}
                onChange={(e) => setReviewForm({ ...reviewForm, review: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="review-rating">Overall Rating</Label>
              <Select 
                value={reviewForm.rating} 
                onValueChange={(value) => setReviewForm({ ...reviewForm, rating: value })}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select rating" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 Stars - Excellent</SelectItem>
                  <SelectItem value="4">4 Stars - Very Good</SelectItem>
                  <SelectItem value="3">3 Stars - Good</SelectItem>
                  <SelectItem value="2">2 Stars - Fair</SelectItem>
                  <SelectItem value="1">1 Star - Poor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button 
              type="submit" 
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-white"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Review"
              )}
            </Button>
          </form>
        </Card>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8 items-center">
        <div className="relative flex-1 w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            type="text"
            placeholder="Search reviews..."
            className="pl-9 pr-4 py-2 w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Select value={filterRating} onValueChange={setFilterRating}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder="Filter by Rating" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Ratings</SelectItem>
            <SelectItem value="5">5 Stars</SelectItem>
            <SelectItem value="4">4 Stars</SelectItem>
            <SelectItem value="3">3 Stars</SelectItem>
            <SelectItem value="2">2 Stars</SelectItem>
            <SelectItem value="1">1 Star</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full sm:w-[180px]">
            {sortBy === "newest" || sortBy === "highest_rating" ? (
              <SortDesc className="mr-2 h-4 w-4" />
            ) : (
              <SortAsc className="mr-2 h-4 w-4" />
            )}
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="oldest">Oldest First</SelectItem>
            <SelectItem value="highest_rating">Highest Rating</SelectItem>
            <SelectItem value="lowest_rating">Lowest Rating</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Reviews List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {currentReviews.length > 0 ? (
            currentReviews.map((review) => (
              <Card key={review.id} className="shadow-sm hover:shadow-md transition-shadow flex flex-col">
                <CardHeader className="flex flex-row items-center gap-4 pb-2">
                  <Avatar>
                    <AvatarImage src={review.avatar || "/placeholder.svg"} alt={review.name} />
                    <AvatarFallback>{review.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <CardTitle className="text-lg font-semibold">{review.name}</CardTitle>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${i < review.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{new Date(review.date).toLocaleDateString()}</p>
                  </div>
                </CardHeader>
                <CardContent className="pt-2 flex-1">
                  <h3 className="font-medium text-gray-800 mb-2">{review.title}</h3>
                  <p className="text-gray-700 text-sm">{review.review}</p>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="col-span-full text-center text-gray-600 text-lg py-10">
              No reviews found matching your criteria.
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                href="#" 
                onClick={(e) => {
                  e.preventDefault()
                  if (currentPage > 1) paginate(currentPage - 1)
                }} 
                className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
            {[...Array(totalPages)].map((_, i) => (
              <PaginationItem key={i}>
                <PaginationLink 
                  href="#" 
                  isActive={currentPage === i + 1} 
                  onClick={(e) => {
                    e.preventDefault()
                    paginate(i + 1)
                  }}
                  className="cursor-pointer"
                >
                  {i + 1}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  if (currentPage < totalPages) paginate(currentPage + 1)
                }}
                className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
      </div>
    </>
  )
}
